import { Download } from "lucide-react";
import React, { useEffect } from "react";
import { ActionIcon, Button } from "@mantine/core";
import { HttpClientError } from "../../../providers/HttpClient";
import useExportSurveyApiService from "../services/api/ExportSurveyApiService";
import { useToasts } from "../../../providers/notifications/ToastsContext";

type SurveyExportButtonProps = {
  id: number;
  asButton: boolean;
};

const ExportSurveyButton: React.FC<SurveyExportButtonProps> = ({
  id,
  asButton,
}) => {
  const { isLoading, isError, error, isSuccess, mutate, data } =
    useExportSurveyApiService();
  const { showToast } = useToasts();

  useEffect(() => {
    if (isError) {
      showToast({
        strapiId: "export_survey_error",
        data: {
          message: HttpClientError(error),
        },
      });
    }
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess && data) {
      const { filename, data: content } = data;

      // Create a Blob from the CSV content
      const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });

      // Create a URL for the Blob
      const url = window.URL.createObjectURL(blob);

      // Create a link element and trigger the download
      const csvLink = document.createElement("a");
      csvLink.href = url;
      csvLink.download = filename;
      document.body.appendChild(csvLink);
      csvLink.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(csvLink);

      showToast({ strapiId: "export_survey_success" });
    }
  }, [isSuccess, data]);

  return (
    <div>
      {asButton ? (
        <Button
          variant="default"
          onClick={() => mutate(id)}
          loading={isLoading}
          loaderPosition="right"
          style={{
            height: "40px",
            flexGrow: 0,
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            padding: "12px 20px",
            borderRadius: "8px",
            backgroundColor: "var(--grey-grey-20)",
            fontFamily: "Inter",
            fontSize: "14px",
            fontWeight: 500,
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: "normal",
            letterSpacing: "0.16px",
            textAlign: "center",
            color: "var(--primary-primary-base)",
          }}
        >
          Download CSV
        </Button>
      ) : (
        <ActionIcon onClick={() => mutate(id)}>
          <Download size={18} />
        </ActionIcon>
      )}
    </div>
  );
};

export default ExportSurveyButton;
